<template>
  <div
    :class="`admin-session-card session-card session-card-status--${sessionStatusClassNamesMap[session.status]}`"
    @click="openSessionDetails"
    data-test-id="admin-session-card"
  >
    <div class="session-card__date">
      <span>{{ extractShortDay(session.from) }}</span>
      <span data-test-id="create-session_expected-day-number">{{ extractDayNumber(session.from) }}</span>
    </div>
    <div class="session-card__content">
      <div class="session-card__content__info">
        <div class="session-card-title">
          <span>{{ session.name ? session.name : t('common.session.capitalize') }}</span>
          <span
            v-if="
              session.status === SessionStatusEnumType.WAIT_CONFIRMATION ||
              session.status === SessionStatusEnumType.WAIT_REVIEW
            "
            class="c-notification-badge"
          ></span>
        </div>
        <div class="session-card__content__info__extra-info">
          <div class="session-card-extra-info">
            <span data-test-id="create_session-formattedExpectedTime" class="session-card-extra-info__duration-range">
              {{ getDurationRange(session.from, session.to) }}
            </span>
            <div class="session-card-extra-info__location">
              <CameraIcon color="black" filled v-if="session.location === 'visio'" />
              <PinIcon color="black" filled v-else />
            </div>
            <!-- <span class="session-card-extra-info__duration-range" v-if="session.deliverables.length > 0">
            Moyenne des notes :
            {{
              Math.round(
                (session?.deliverables
                  .filter((deliverable) => typeof deliverable.rating === 'number')
                  .reduce((acc, deliverable) => acc + (deliverable.rating ?? 0), 0) /
                  session?.deliverables.filter((deliverable) => typeof deliverable.rating === 'number').length) *
                  10,
              ) / 10
            }}/20
          </span> -->
            <Badge :variant="badgePropsRef.variant">
              {{ t(badgePropsRef.textKey) }}
            </Badge>
          </div>
          <div class="session-card-group-name">
            <span :title="session.mentoringGroupName" v-if="session.mentoringGroupName" class="c-group-name">
              {{ cropedGroupNameRef }}
            </span>
          </div>
        </div>
      </div>
      <div class="session-card__users">
        <UserBadge v-if="mentorRef" :user="mentorRef" />
        <UsersBadgesStack :users="menteesRef" />
      </div>
    </div>
    <div
      class="session-card__options"
      data-test-id="session-card-options"
      data-action="session-card"
      :data-attached="JSON.stringify(session)"
    >
      <MoreOptionsIcon color="black" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { getMentees, getMentor } from '@/features/groups/utils'
import UserBadge from '@/features/sessions/components/user-badge/user-badge.vue'
import UsersBadgesStack from '@/features/sessions/components/users-badges-stack/users-badges-stack.vue'
import type { V2SessionType } from '@/features/sessions/types'
import { SessionStatusEnumType } from '@/features/sessions/types'
import { sessionStatusClassNamesMap, getSessionStatusBadgeProps } from '@/features/sessions/utils'
import CameraIcon from '@/features/ui/components/icons/camera-icon.vue'
import MoreOptionsIcon from '@/features/ui/components/icons/more-options-icon.vue'
import PinIcon from '@/features/ui/components/icons/pin-icon.vue'
import Badge from '@/components/Badge.vue'
import type { SessionDetailsModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { useI18n } from '@/lib/i18n'
import { extractDayNumber, extractShortDay, getDurationRange } from '@/utils/dates'

export type AdminSessionCardType = V2SessionType & { status: SessionStatusEnumType }

const props = defineProps<{
  session: AdminSessionCardType
}>()
const { t } = useI18n()

const mentorRef = computed(() => props.session.users.find(getMentor))
const menteesRef = computed(() => props.session.users.filter(getMentees))

const badgePropsRef = computed(() => getSessionStatusBadgeProps(props.session.status))

const cropedGroupNameRef = computed(() => {
  const name = props.session.mentoringGroupName
  if (!name) return ''

  // Si c'est un nom par défaut (on suppose que ce sont des prénoms séparés par des virgules)
  const parts = name.split(',').map((p) => p.trim())
  const isDefault = parts.length > 1 && parts.every((p) => /^[A-Za-zÀ-ÿ'-]+$/.test(p))

  if (isDefault) {
    const visible = parts.slice(0, 3)
    const hiddenCount = parts.length - visible.length
    return hiddenCount > 0 ? `${visible.join(', ')}, +${hiddenCount}` : visible.join(', ')
  } else {
    return name.length > 20 ? name.slice(0, 20) + '...' : name
  }
})

function openSessionDetails(e: MouseEvent) {
  // guard to not open the modal when clicking on the more options icon
  if ((e.target as HTMLElement).closest('[data-action="session-card"]')) {
    return
  }

  modalsStore.updateModalAttachedData<SessionDetailsModalType>('session-details', {
    sessionId: props.session.id,
  })
  modalsStore.toggleModal('session-details', true)
}
</script>

<style lang="scss">
@import './session-card.scss';
</style>
